/**
 * MissedEnquiriesPanel — follow-up list for unanswered IVR enquiry calls.
 *
 * Surfaces missed / forwarded enquiry calls (IVR options 2 Driver recruitment,
 * 3 Dispatch OS sales, 4 General enquiries). When a forward goes unanswered the
 * backend auto-sends a tailored SMS; this view lets the team chase every lead.
 * Read-only. No dispatch/booking/pricing logic touched.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  PhoneMissed, PhoneForwarded, RefreshCw, Loader2, MessageSquareText,
  CheckCircle2, XCircle, Clock,
} from "lucide-react";

const OPTION_STYLES = {
  "2": "bg-[#FFF7ED] text-[#9A3412] border-[#FDBA74]",
  "3": "bg-[#EFF6FF] text-[#1E40AF] border-[#93C5FD]",
  "4": "bg-[#F5F3FF] text-[#5B21B6] border-[#C4B5FD]",
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const SmsBadge = ({ row }) => {
  if (row.answered) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="sms-badge-answered">
        <PhoneForwarded className="w-3 h-3" /> Answered
      </span>
    );
  }
  if (row.sms_sent) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-[#065F46]" data-testid="sms-badge-sent">
        <CheckCircle2 className="w-3 h-3" /> SMS sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-[#92400E]" data-testid="sms-badge-notsent" title={row.sms_status || ""}>
      <XCircle className="w-3 h-3" /> {row.sms_status ? row.sms_status.replace(/_/g, " ") : "no SMS"}
    </span>
  );
};

export const MissedEnquiriesPanel = () => {
  const [data, setData] = useState({ items: [], total: 0, missed_count: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/missed-enquiries");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load missed enquiries");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6" data-testid="missed-enquiries-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-heading text-2xl font-black tracking-tight flex items-center gap-2">
            <PhoneMissed className="w-6 h-6 text-[#EF4444]" /> Missed Calls
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Enquiry calls (Drive with us · Dispatch OS · General) that were forwarded but
            unanswered. Each one automatically receives a follow-up SMS — chase any lead below.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="missed-refresh" title="Refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="border border-border p-3 bg-white" data-testid="stat-total">
          <div className="label-mini text-muted-foreground">TOTAL ENQUIRIES</div>
          <div className="text-2xl font-heading font-black">{data.total}</div>
        </div>
        <div className="border border-[#FCA5A5] bg-[#FEF2F2] p-3" data-testid="stat-missed">
          <div className="label-mini text-[#991B1B]">MISSED</div>
          <div className="text-2xl font-heading font-black text-[#991B1B]">{data.missed_count}</div>
        </div>
        <div className="border border-[#10B981] bg-[#ECFDF5] p-3" data-testid="stat-sms">
          <div className="label-mini text-[#065F46]">SMS FOLLOW-UPS</div>
          <div className="text-2xl font-heading font-black text-[#065F46]">
            {data.items.filter((i) => i.sms_sent).length}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      ) : data.items.length === 0 ? (
        <div className="border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-testid="missed-empty">
          No enquiry calls yet. Missed forwards will appear here with their follow-up SMS status.
        </div>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">When</th>
                <th className="text-left px-3 py-2 font-semibold">Caller</th>
                <th className="text-left px-3 py-2 font-semibold">Enquiry</th>
                <th className="text-left px-3 py-2 font-semibold">Rang</th>
                <th className="text-left px-3 py-2 font-semibold">Follow-up</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.items.map((r) => (
                <tr key={r.id} data-testid={`missed-row-${r.id}`} className={r.answered ? "" : "bg-[#FFFBFA]"}>
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(r.ts)}</span>
                  </td>
                  <td className="px-3 py-2.5 font-mono-tab" data-testid={`missed-caller-${r.id}`}>
                    {r.caller ? (
                      <a href={`tel:${r.caller}`} className="hover:underline">{r.caller}</a>
                    ) : <span className="text-muted-foreground">unknown</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 border text-[11px] font-semibold ${OPTION_STYLES[r.option] || "bg-secondary border-border"}`}>
                      {r.option_label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                    {r.call_duration_sec ? `${r.call_duration_sec}s` : "—"}
                    <span className="ml-1 text-[11px]">({r.dial_status || "—"})</span>
                  </td>
                  <td className="px-3 py-2.5"><SmsBadge row={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 text-[11px] text-muted-foreground">
        <MessageSquareText className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>Follow-up SMS is sent once per missed call via the existing Twilio gateway. Caller numbers link to dial back directly.</span>
      </div>
    </div>
  );
};

export default MissedEnquiriesPanel;
